import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import authService from '../services/AuthService';
import { clearMeCache } from '../services/MeCache';
import userService from '../services/UserService';
import {
  TENANT_CHANGED_EVENT,
  canSwitchTenant,
  clearActingAccountId,
  getActingAccountId,
  getHomeAccountId,
  setActingAccountId,
} from '../services/TenantService';

const STORAGE_KEY = 'user';
const SESSION_DURATION_MS = 6 * 24 * 60 * 60 * 1000; // 6 days in milliseconds

function readStoredUser(): { user: any; isAuthenticated: boolean } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { user: null, isAuthenticated: false };
    const data = JSON.parse(raw);
    const timestamp = data.timestamp;
    if (timestamp != null && timestamp + SESSION_DURATION_MS < Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return { user: null, isAuthenticated: false };
    }
    return { user: data, isAuthenticated: true };
  } catch {
    return { user: null, isAuthenticated: false };
  }
}

/**
 * The login response is persisted whole, so the account itself sits one level
 * down under `user`. Older stored sessions may carry the raw column name.
 */
function extractUserRoleId(stored: any): number | null {
  const raw =
    stored?.user?.userRoleId ??
    stored?.user?.UserRoleID ??
    stored?.userRoleId ??
    stored?.UserRoleID;
  if (raw == null) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

interface AuthContextValue {
  isAuthenticated: boolean;
  user: any;
  userRoleId: number | null;
  /** The account the user belongs to. */
  homeAccountId: number | null;
  /**
   * True when the user may switch between bakeries: an admin role, on any
   * account. Everyone else is confined to their own tenant and gets no
   * selector.
   *
   * Named for the old account-based rule; the rule is now role-only.
   */
  isPlatformAdmin: boolean;
  /** The bakery currently being viewed; equals homeAccountId for tenant users. */
  actingAccountId: number | null;
  /** Switch acting bakery. No-op for anyone without an admin role. */
  switchAccount: (accountId: number | null) => void;
  login: (payload: { email: string; password: string }) => Promise<any>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState(() => {
    const stored = readStoredUser();
    if (stored.isAuthenticated && stored.user) {
      userService.init({ user: stored.user });
    }
    return stored;
  });
  const [tenantTick, setTenantTick] = useState(0);

  const login = useCallback(async (payload: { email: string; password: string }) => {
    const user = await authService.login(payload);
    if (user?.token?.length > 0) {
      setState({ user, isAuthenticated: true });
      return user;
    }
    return user;
  }, []);

  const logout = useCallback(() => {
    // Drop the acting bakery too, or the next user to log in on this browser
    // would inherit the previous admin's selection.
    clearActingAccountId();
    // And the cached identity, so the app bar cannot briefly show the previous
    // user's name and tenant to whoever logs in next on this tab.
    clearMeCache();
    authService.logout();
    setState({ user: null, isAuthenticated: false });
  }, []);

  const switchAccount = useCallback((accountId: number | null) => {
    setActingAccountId(accountId);
    setTenantTick((n) => n + 1);
  }, []);

  // Keep in step with switches made elsewhere (other tabs, direct service calls).
  useEffect(() => {
    const onChange = () => setTenantTick((n) => n + 1);
    window.addEventListener(TENANT_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(TENANT_CHANGED_EVENT, onChange);
  }, []);

  // Drop any acting bakery left in localStorage once the signed-in user may not
  // switch: someone demoted out of an admin role, or a different user logging
  // in on a browser an admin used before. getActingAccountId() already ignores
  // the key in that case, but clearing it keeps the stored state honest and
  // stops a stale value reappearing if the user is later promoted.
  useEffect(() => {
    if (state.isAuthenticated && !canSwitchTenant()) clearActingAccountId();
  }, [state.isAuthenticated, state.user]);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      userRoleId: extractUserRoleId(state.user),
      homeAccountId: getHomeAccountId(),
      isPlatformAdmin: canSwitchTenant(),
      actingAccountId: getActingAccountId(),
      switchAccount,
      login,
      logout,
    }),
    // tenantTick re-reads the tenant getters, which are backed by localStorage
    // rather than React state.
    [state.isAuthenticated, state.user, tenantTick, switchAccount, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
